import type { ReactNode } from 'react';
import { cn } from '#client/lib/cn';

interface TabItem<T extends string> {
  value: T;
  label: string;
  /** Rendered as a count pill beside the label. */
  count?: number | undefined;
  /** A small red dot, for "some of these need attention". */
  pip?: boolean | undefined;
}

interface TabsProps<T extends string> {
  label: string;
  items: readonly TabItem<T>[];
  value: T;
  onChange: (value: T) => void;
  className?: string | undefined;
}

/**
 * Status tabs over a single table.
 *
 * This is the dominant worklist pattern across diagnostic-lab software, and its
 * absence is the single most-cited complaint about one of the incumbents —
 * staff scrolling past finished work every day because there was no way to
 * segment it.
 *
 * Counts live on the tabs so the size of each queue is visible without
 * switching to it. They come back with the page in one response rather than
 * three extra requests.
 *
 * `role="tablist"` with arrow-key navigation and a roving tabindex, so the
 * whole bar is one tab stop.
 */
export function Tabs<T extends string>({ label, items, value, onChange, className }: TabsProps<T>) {
  const moveTo = (index: number) => {
    const next = items[(index + items.length) % items.length];
    if (next) onChange(next.value);
  };

  return (
    <div role="tablist" aria-label={label} className={cn('c-tabs', className)}>
      {items.map((item, index) => {
        const selected = item.value === value;

        return (
          <button
            key={item.value}
            type="button"
            role="tab"
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            className={cn('c-tabs__tab', selected && 'is-selected')}
            onClick={() => onChange(item.value)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowRight') {
                event.preventDefault();
                moveTo(index + 1);
              } else if (event.key === 'ArrowLeft') {
                event.preventDefault();
                moveTo(index - 1);
              }
            }}
          >
            <span>{item.label}</span>

            {item.count !== undefined && <span className="c-tabs__count">{item.count}</span>}

            {item.pip && (
              <span className="c-tabs__pip" title="Some of these are past due">
                {/* Text for anyone not seeing the dot — colour alone is never
                    sufficient. */}
                <span className="sr-only">Contains overdue work</span>
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/** A filter chip that toggles on and off. */
export function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      // A toggle, so its state must be announced rather than merely styled.
      aria-pressed={active}
      className={cn('c-chip', active && 'is-active')}
      onClick={onClick}
    >
      {children}
    </button>
  );
}
